import {
  Component, OnInit, Output, EventEmitter, Input, ChangeDetectionStrategy,
  ChangeDetectorRef, OnDestroy
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, Subject, merge, combineLatest } from 'rxjs';
import { takeUntil, take, map } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';
import { Actions, ofType } from '@ngrx/effects';

import { BillableItem } from 'modules/shared/models/billable.models';
import { Screening, QuickScan, SearchEngineTypeValue, OngoingDueDiligence, SearchItem } from 'modules/shared/models/screening.models';
import { SearchResultResponse, BatchUpdateSearchResultPayload, ScreeningErrorsPayload } from 'store/models/screening.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { Crp } from 'modules/shared/models/crp.models';
import { ScreeningErrors } from 'modules/shared/models/screening.models';

import * as ScreeningActions from 'store/actions/search.actions';

@Component({
  selector: 'artemis-searches',
  templateUrl: './searches.component.html',
  styleUrls: ['./searches.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchesComponent implements OnInit, OnDestroy {

  private unsubscribe$: Subject<void> = new Subject();

  record: IndividualRecord | CorporateRecord;

  searchEngines: Array<BillableItem>;
  selectedEngine: BillableItem;

  engineType: any = SearchEngineTypeValue;
  screening: Screening;
  selectedEngineData: SearchResultResponse | SearchItem;

  quickScan: QuickScan;
  ongoingDueDiligence: OngoingDueDiligence;
  asyncSearchEngines: SearchEngineTypeValue[];
  screeningStatus: {
    [key in SearchEngineTypeValue]?: boolean;
  } = {};

  errors: Array<SearchEngineTypeValue> = [];
  invalidSearches: Array<SearchEngineTypeValue> = [];

  loadingAll$: Observable<boolean>;
  currentCRP$: Observable<Crp> = this.store.select(Selectors.customer.getCurrentCrp);

  _recordName: string;
  isOngoingDueDiligence: boolean;

  @Input()
  set searchEnginesList(data: Array<BillableItem>) {
    if (!data) {
      return;
    }

    this.searchEngines = data;

    if (!this.selectedEngine) {
      this.selectedEngine = this.searchEngines[0];
    }
  }

  @Input()
  set databaseSearch(data: Screening | QuickScan | OngoingDueDiligence) {
    if (data && (Object.entries(data).length !== 0)) {
      this.store.select(Selectors.billable.getSearchEngineTypes).pipe(
        take(1)
      ).subscribe((engineTypes: SearchEngineTypeValue[]) => {
        this.asyncSearchEngines = engineTypes;
        this.asyncSearchEngines.forEach((engine: SearchEngineTypeValue) => {
          this.screeningStatus[engine] = false;
        });
        Object.keys(this.screeningStatus).forEach((key: string) => {
          if (data[key] && data[key].results && data[key].results[0]) {
            this.screeningStatus[key] = data[key].results[0].isSearchFinished;
          } else {
            this.screeningStatus[key] = true;
          }
        });
        if (this.isScreening(data)) {
          this.quickScan = null;
          this.ongoingDueDiligence = null;
          this.screening = data;
          this.isOngoingDueDiligence = false;
          this.getInvalidSearches();
          this.setSelectedEngineData();
        } else if (this.isQuickScan(data)) {
          this.screening = null;
          this.ongoingDueDiligence = null;
          this.quickScan = data;
          this.isOngoingDueDiligence = false;
        } else {
          this.screening = null;
          this.quickScan = null;
          this.ongoingDueDiligence = data;
          if (this.ongoingDueDiligence && Object.keys(this.ongoingDueDiligence).length > 0) {
            this.isOngoingDueDiligence = true;
          } else {
            this.isOngoingDueDiligence = false;
          }
        }
        this.cdf.markForCheck();
      });

    }
  }

  @Input() dataSource: any;

  @Input()
  set recordName(value: string) {
    this._recordName = '';
    if (value) {
      this._recordName = value;
    }
  }

  get recordName(): string {
    return this._recordName;
  }

  @Output() refresh: EventEmitter<string> = new EventEmitter();
  @Output() refreshAll: EventEmitter<void> = new EventEmitter();
  @Output() getUpdatedScreeningResult: EventEmitter<string> = new EventEmitter();

  constructor(private cdf: ChangeDetectorRef,
              private store: Store<AppState>,
              private actions$: Actions) {
  }

  ngOnInit() {
    this.getLoadingStatus();

    combineLatest([
      merge(
        this.store.select(Selectors.record.getCorporateRecord),
        this.store.select(Selectors.record.getIndividualRecord)
      ),
      this.store.select(Selectors.error.getScreeningErrors)
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([res, error]: [CorporateRecord | IndividualRecord, ScreeningErrors]) => {
        if (res && error) {
          this.record = res;
          this.errors = error[res.id] ? error[res.id].data.failedSearches : [];
        }
        this.cdf.detectChanges();
      });

    this.getScreeningResultWhenUpdated();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loadingAll$ = this.store.select(Selectors.search.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((screeningLoading: Array<ScreeningActions.SearchActionTypes>) => {
          return screeningLoading.includes(ScreeningActions.SearchActionTypes.POST_SCREENING) ||
                 screeningLoading.includes(ScreeningActions.SearchActionTypes.GET_SCREENING);
      }));
  }

  /**
   * Emit the event to get screening result when comment or matched status is updated
   * @method getScreeningResultWhenUpdated
   */
  private getScreeningResultWhenUpdated(): void {
    this.actions$.pipe(
      ofType<ScreeningActions.PatchSearchResultSuccess>(ScreeningActions.SearchActionTypes.PATCH_SEARCH_RESULT_SUCCESS),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      () => {
        this.getUpdatedScreeningResult.emit(this.selectedEngine.serviceType);
      }
    );
  }

  private isScreening(data: Screening | QuickScan | OngoingDueDiligence): data is Screening {
    return (<Screening>data).isScreening !== undefined;
  }

  private isQuickScan(data: Screening | QuickScan | OngoingDueDiligence): data is QuickScan {
    return (<QuickScan>data).isQuickScan !== undefined;
  }

  private setSelectedEngineData(): void {
    const currentDataSource: any = this.screening || this.quickScan || this.ongoingDueDiligence;
    this.selectedEngineData = currentDataSource[this.selectedEngine.serviceType];
  }

  onRefreshItemClicked() {
    this.refresh.emit(this.selectedEngine.serviceType);
  }

  onRefreshAllClicked() {
    this.refreshAll.emit();
  }

  onSelectSearchEngine(searchEngine: BillableItem): void {
    this.selectedEngine = searchEngine;
    this.setSelectedEngineData();
  }

  usePreviousSearch(searchEngine?: BillableItem): void {
    this.store.dispatch(new RootActions.error.RemoveScreeningError(<ScreeningErrorsPayload>{
      recordId: this.record.id,
      searchEngineType: (searchEngine || this.selectedEngine).serviceType
    }));
  }

  getInvalidSearches(): void {
    Object.keys(this.screening).forEach((key: string) => {
      if (this.screening[key] && this.screening[key].results && this.screening[key].results.length) {
        if (this.screening[key].results[0].valid) {
          this.invalidSearches = this.invalidSearches.filter((invalidSearch: string) => invalidSearch !== this.screening[key].engineType);
        } else {
          this.invalidSearches.push(this.screening[key].engineType);
        }
      }
    });

    this.cdf.detectChanges();
  }

  onScreeningCommentSubmitted(payload: BatchUpdateSearchResultPayload) {
    this.store.dispatch(new RootActions.search.PatchSearchResult(payload));
  }
}
